import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { BornAggregazioneCrumbs } from 'subscription/constants/breadcrumb';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { toast } from 'react-toastify';
import { ItemVM } from 'shell/view-models/item';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import validationSchemaBornAggregation from './validation-schema';
import PmiTitle from 'shell/components/shared/title';
import SubscriptionButtons from 'subscription/components/shared/buttons';
import {
  InitialValuesAccordionFamilyCoexistence,
  InitialValuesEditDataControl,
} from 'subscription/constants/subscription-birth-initial-values';
import { CODE_STATE_ITALIAN } from 'shell/constants/code-state-italian';
import { IDFlags } from 'shell/components/shared/flag-validation-fiscal-code/flag';
import SubscriptionTranscriptionBirthAct from 'subscription/components/transcription-birth-act';
import { SubscriptionTranscriptionBirthActFormVM } from 'subscription/view-models/subscription-transcription-birth-act';
import { SubscriptionGainCitizenshipFormVM } from 'subscription/view-models/subscription-gain-citizenship';
import { transformerSubscriptionTranscriptionBirthActData } from 'subscription/utils/transcription-birth-act.response-transformer';
import { transformerRequestSubscriptionTranscriptionBirthActAireData } from 'subscription/utils/transcription-birth-act.request-transformer';

/**
 * BornAggregazione component
 *
 * This component is responsible for rendering the UI for the "Iscrizione AIRE atto di nascita - Aggregazione" page.
 *
 * It handles the following:
 * - fetching data from the API
 * - rendering the UI based on the fetched data
 * - handling errors and warnings
 * - handling form submission
 *
 * @returns {React.ReactElement} The rendered UI
 */
const BornAggregazione = (): React.ReactElement => {
  const navigate = useNavigate();

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [queryParams] = useSearchParams();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, SubscriptionGainCitizenshipFormVM>(
    `${__PERSON_NEW_API_URL__}/persona/ricerca-famiglia-aggregazione`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
            soggettoAIRE: 'S',
          },
          datiRichiesta: {
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            ricercaNazionale: nationalSearch ? '1' : '0',
          },
        },
      },
      transformer: (response) => transformerSubscriptionTranscriptionBirthActData(response, true),
    }
  );

  const [responseRegister, register] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/iscrizione-nascita/aire`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseRegister?.data as SaveDataAnpr;

  const handleSubmit = useEventCallback((values: SubscriptionTranscriptionBirthActFormVM) => {
    const data = transformerRequestSubscriptionTranscriptionBirthActAireData(values);
    (
      register({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
    ).then((response) => {
      const castedResponse = response as SaveDataAnpr;
      setVisibility({ error: undefined, warning: undefined });

      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      toast.success('Operazione eseguita con successo');
      navigate('/iscrizioni');
    });
  });

  const handleExit = useCallback(() => {
    navigate('/iscrizioni');
  }, [navigate]);

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  //Errors
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  const data = (anpr?.data || {}) as SubscriptionTranscriptionBirthActFormVM;

  return (
    <Fragment>
      <Breadcrumbs items={BornAggregazioneCrumbs} />
      <div>
        <PmiTitle title="Iscrizione AIRE atto di nascita - Aggregazione" />
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedSavedData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      {anpr.data && !anpr.isLoading && !anpr.hasErrors ? (
        <Formik
          initialValues={
            {
              ...anpr.data,
              familyCoexistence: {
                ...InitialValuesAccordionFamilyCoexistence,
                ...data?.familyCoexistence,
                disabledDate: true,
              },
              citizenship: {
                ...data?.citizenship,
                state: {
                  id: CODE_STATE_ITALIAN,
                  value: 'ITALIA',
                },
              },
              editDataControl: {
                ...InitialValuesEditDataControl,
                ...data?.editDataControl,
                flag: IDFlags.Attribuzione,
                subscribeTo: '5', // "Altra famiglia" fisso
              },
              generality: {
                ...data?.generality,
                aire: 'S',
                isForeign: true,
              },
              birthdayDocument: {
                ...data?.birthdayDocument,
                paper: data?.birthdayDocument?.paper
                  ? {
                      ...data?.birthdayDocument?.paper,
                      transcribed: true,
                    }
                  : undefined,
              },
            } as SubscriptionTranscriptionBirthActFormVM
          }
          onSubmit={handleSubmit}
          validationSchema={validationSchemaBornAggregation}
        >
          <Form>
            <Grid container spacing={2}>
              <SubscriptionButtons handleExit={handleExit} />
              <Grid item xs={12} md={10}>
                <SubscriptionTranscriptionBirthAct
                  isAire
                  hasResearchSipo
                  urlEditSipo="/iscrizioni/aire-nascita-aggregazione/persona-sipo"
                  urlBackendSipo="/non-residenti/ricerca-intestatario-nascita-aire"
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      ) : undefined}
    </Fragment>
  );
};

BornAggregazione.displayName = 'Born Aggregazione page component';

export default BornAggregazione;
